import { Tabs } from '@design-systems/canvas-ui'
import { useMemo } from 'react'
import { MPMLayout } from 'components/layout'
import { PageTabs } from 'components/page-tabs'
import { useEssentials } from 'hooks/utils/useEssentials'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { ErrorBoundary } from 'react-error-boundary'
import { ErrorFallback } from 'components/error-fallback'
import { useDwellTime, useLogClick } from 'utils/analytics'
import { InsightsTab } from './utils'
import './insights.scss'

export const InsightsPage = () => {
    const { insights } = TRANSLATION_KEYS
    const { tabs: tabsKeys } = insights
    const navigate = useNavigate()
    const locationFromRouter = useLocation()
    const { t } = useEssentials()
    const { logClick } = useLogClick('insights')
    useDwellTime('insights')

    const tabsWithTranslations = useMemo(
        () => [
            { key: InsightsTab.Yesterday, label: t(tabsKeys.yesterday) },
            { key: InsightsTab.Weekly, label: t(tabsKeys.weekly) },
        ],
        [t, tabsKeys],
    )

    const activeTab = useMemo(() => {
        const path = locationFromRouter.pathname.split('/').pop()
        return (
            tabsWithTranslations.find((tab) => tab.key === path)?.key ??
            InsightsTab.Yesterday
        )
    }, [locationFromRouter.pathname, tabsWithTranslations])

    const handleTabSelect = (selected: string) => {
        logClick(`insights ${selected}`)
        void navigate(`/insights/${selected}${locationFromRouter.search}`, {
            replace: true,
        })
    }

    return (
        <ErrorBoundary FallbackComponent={ErrorFallback}>
            <MPMLayout
                heading={t(insights.title)}
                className="hc8-ph-none insights-page"
                breadcrumbsEnabled={false}
                hasTabs
                tabs={
                    <PageTabs
                        activeTab={activeTab}
                        className="tabs hc8-pv-2x"
                        disableAdaptive={true}
                        onTabSelect={handleTabSelect}
                    >
                        {tabsWithTranslations.map((tab) => (
                            <Tabs.Item key={tab.key} name={tab.key}>
                                {tab.label}
                            </Tabs.Item>
                        ))}
                    </PageTabs>
                }
            >
                <div id="canvasPortalContainer">
                    <Outlet />
                </div>
            </MPMLayout>
        </ErrorBoundary>
    )
}
